import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { tokens as c } from '../../theme';
import IconCircle from './IconCircle';

/**
 * The top of a page or tab, under the shell's orange bar (which already
 * says the page name as the h1): an optional section title, a count line,
 * then the secondary actions and ONE primary action on the right.
 *
 *   <PageHeader
 *     count={t('customers_count', { count: 120, defaultValue: '{{count}} customers' })}
 *     primaryAction={{ label: t('add_customer'), icon: <AddRoundedIcon />, onClick: addCustomer, color: 'success' }}
 *     secondaryActions={[{ key: 'onboard', label: t('unified_driver_onboarding'), icon: <PersonAddAltRoundedIcon />, onClick: goOnboard }]}
 *   />
 *
 * title      optional h2 for a section (a tab already shows its own name: leave it out there)
 * count      the "412 bookings · last 7 days" line (string or node); announced politely
 * icon / hue an icon on a tinted circle before the title
 * primaryAction   { label, icon, onClick, color: 'primary' | 'success', disabled, busy, show }
 * secondaryActions [{ key, label, icon, onClick, disabled, show }] (grey outlined buttons)
 * children   anything else for the right side (a select, a date range)
 * Actions with show: false are not drawn (role gates stay in the page).
 */
export default function PageHeader({ title, count, icon, hue = 'accent', primaryAction, secondaryActions, children, titleId, sx }) {
  const secondaries = (Array.isArray(secondaryActions) ? secondaryActions : []).filter((a) => a && a.show !== false);
  const primary = primaryAction && primaryAction.show !== false ? primaryAction : null;
  const hasRight = secondaries.length > 0 || !!primary || !!children;
  if (!title && !count && !hasRight) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 1.5,
        ...(sx || {}),
      }}
    >
      {title || count ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: '1 1 240px' }}>
          {icon && title ? <IconCircle icon={icon} hue={hue} size={36} /> : null}
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Typography
                id={titleId}
                component="h2"
                sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}
              >
                {title}
              </Typography>
            ) : null}
            {count ? (
              <Typography
                component="p"
                role="status"
                aria-live="polite"
                sx={{
                  fontSize: title ? 14 : 15,
                  fontWeight: title ? 400 : 700,
                  lineHeight: 1.5,
                  color: title ? c.TEXT_3 : c.INK,
                  fontVariantNumeric: 'tabular-nums',
                  overflowWrap: 'anywhere',
                }}
              >
                {count}
              </Typography>
            ) : null}
          </Box>
        </Box>
      ) : null}
      {hasRight ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, marginInlineStart: 'auto', justifyContent: 'flex-end' }}>
          {children}
          {secondaries.map((a) => (
            <Button
              key={a.key || a.label}
              variant="outlined"
              color="neutral"
              startIcon={a.icon || undefined}
              onClick={a.onClick}
              disabled={!!a.disabled}
            >
              {a.label}
            </Button>
          ))}
          {primary ? (
            <Button
              variant="contained"
              color={primary.color === 'success' ? 'success' : 'primary'}
              startIcon={primary.busy ? <CircularProgress size={16} color="inherit" /> : primary.icon || undefined}
              onClick={primary.onClick}
              disabled={!!primary.disabled || !!primary.busy}
            >
              {primary.label}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
